import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "@/hooks/use-theme";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { PWAInstallPrompt } from "@/components/pwa/PWAInstallPrompt";
import { PWAUpdatePrompt } from "@/components/pwa/PWAUpdatePrompt";
import { OfflineBanner } from "@/components/pwa/OfflineBanner";
import { Skeleton } from "@/components/ui/skeleton";

// Lazy-loaded route components
const Landing = lazy(() => import("./pages/Landing"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Auth = lazy(() => import("./pages/Auth"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Privacy = lazy(() => import("./pages/legal/Privacy"));
const Terms = lazy(() => import("./pages/legal/Terms"));
const Cookies = lazy(() => import("./pages/legal/Cookies"));
const Feed = lazy(() => import("./pages/app/Feed"));
const Library = lazy(() => import("./pages/app/Library"));
const Setup = lazy(() => import("./pages/app/Setup"));
const Tasks = lazy(() => import("./pages/app/Tasks"));
const SOPs = lazy(() => import("./pages/app/SOPs"));
const SOPDetail = lazy(() => import("./pages/app/SOPDetail"));
const Runs = lazy(() => import("./pages/app/Runs"));
const RunDetail = lazy(() => import("./pages/app/RunDetail"));
const Analytics = lazy(() => import("./pages/app/Analytics"));
const Settings = lazy(() => import("./pages/app/Settings"));
const Activity = lazy(() => import("./pages/app/Activity"));
const Schedules = lazy(() => import("./pages/app/Schedules"));
const NotFound = lazy(() => import("./pages/NotFound"));

// AppLayout is not lazy-loaded since it wraps child routes
import { AppLayout } from "./components/layout/AppLayout";

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="space-y-4 w-full max-w-md px-8">
      <Skeleton className="h-8 w-3/4 mx-auto" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-5/6" />
      <Skeleton className="h-32 w-full rounded-xl" />
    </div>
  </div>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      retry: 2,
      refetchOnWindowFocus: false,
    },
  },
});

const App = () => (
  <ErrorBoundary>
    <ThemeProvider defaultTheme="dark" storageKey="ritual-ui-theme">
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <OfflineBanner />
          <Toaster />
          <Sonner />
          <PWAInstallPrompt />
          <PWAUpdatePrompt />
          <BrowserRouter>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/onboarding" element={<Onboarding />} />

                {/* Company Pages */}
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />

                {/* Legal Pages */}
                <Route path="/legal/privacy" element={<Privacy />} />
                <Route path="/legal/terms" element={<Terms />} />
                <Route path="/legal/cookies" element={<Cookies />} />

                {/* App Routes */}
                <Route path="/app" element={<AppLayout />}>
                  <Route index element={<Feed />} />
                  <Route path="feed" element={<Feed />} />
                  <Route path="tasks" element={<Tasks />} />
                  <Route path="library" element={<Library />} />
                  <Route path="setup" element={<Setup />} />
                  <Route path="sops" element={<SOPs />} />
                  <Route path="sops/:id" element={<SOPDetail />} />
                  <Route path="runs" element={<Runs />} />
                  <Route path="runs/:id" element={<RunDetail />} />
                  <Route path="analytics" element={<Analytics />} />
                  <Route path="activity" element={<Activity />} />
                  <Route path="schedules" element={<Schedules />} />
                  <Route path="settings" element={<Settings />} />
                </Route>

                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </ErrorBoundary>
);

export default App;
